import { FC, memo } from "react";
import { Link } from "react-router-dom";
import { Col, Layout, Row } from "antd";
import {
  DoubleRightOutlined,
  InstagramOutlined,
  LinkedinFilled,
  YoutubeFilled,
} from "@ant-design/icons";
import { Logo } from "../../../assets/images";
import { ServicesConstant, UsefulLinksConstant } from "./MainFooter.constant";
import { PageRoutesConstant } from "../router/components/PageRoutes";
import "./MainFooter.style.scss";

const { Footer } = Layout;

const FOOTER_ADDRESS =
  "B 712-713, IT Park, Opp. Shell Petrol Pump, Mota Varachha Main Road, Uttran, Surat - 394105, Gujarat, India";

const MainFooter: FC = () => {
  return (
    <Footer className="footer" role="contentinfo">
      <div className="container-md">
        <Row
          gutter={[24, 24]}
          justify="space-between"
          className="footer_content"
        >
          <Col xs={24} md={12} lg={8}>
            <CompanyDetails />
          </Col>
          <Col xs={10} sm={12} md={12} lg={4}>
            <UsefulLinks />
          </Col>
          <Col xs={14} sm={12} md={12} lg={6}>
            <OurServices />
          </Col>
          <Col xs={24} md={12} lg={6}>
            <SocialLink />
          </Col>
        </Row>
        <Copyright />
      </div>
      <OrganizationSchema />
    </Footer>
  );
};

const CompanyDetails: FC = memo(function CompanyDetails() {
  const mapUrl = process.env.REACT_APP_GOOGLE_MAPS_LOCATION;
  const email = process.env.REACT_APP_EMAIL;
  const phone1 = process.env.REACT_APP_PHONE1;
  const phone2 = process.env.REACT_APP_PHONE2;

  return (
    <div className="mb-3">
      <div className="footer_content_logo_wrapper mb-3">
        <Link
          to={PageRoutesConstant.Home.path}
          aria-label="BIGELEMENT INFOTECH - Home"
        >
          <img
            src={Logo}
            alt="BIGELEMENT INFOTECH - Software Development Company"
            className="img-fluid footer_content_logo"
            width={140}
            height={40}
            loading="lazy"
            decoding="async"
          />
        </Link>
      </div>
      <address className="footer_content_address">
        <div className="mb-3">
          <strong>Address: </strong>
          <a
            href={mapUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="footer_content_details"
            aria-label="View our office location on Google Maps"
          >
            {FOOTER_ADDRESS}
          </a>
        </div>
        <div className="mb-1">
          <strong>Phone: </strong>
          <span className="footer_content_phones">
            {phone1 && (
              <a href={`tel:${phone1}`} className="footer_content_details">
                {phone1}
              </a>
            )}
            {phone1 && phone2 && " / "}
            {phone2 && (
              <a href={`tel:${phone2}`} className="footer_content_details">
                {phone2}
              </a>
            )}
          </span>
        </div>
        <div>
          <strong>Email: </strong>
          <a href={`mailto:${email}`} className="footer_content_details">
            {email}
          </a>
        </div>
      </address>
    </div>
  );
});

const UsefulLinks: FC = memo(function UsefulLinks() {
  return (
    <nav aria-label="Useful links">
      <div className="footer_content_title mb-2">Useful Links</div>
      <ul className="footer_content_list">
        {UsefulLinksConstant.map((item) => (
          <li key={item.path}>
            <Link to={item.path} className="footer_content_list_link">
              <DoubleRightOutlined aria-hidden /> {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
});

const OurServices: FC = memo(function OurServices() {
  return (
    <nav aria-label="Our services">
      <div className="footer_content_title mb-2">Our Services</div>
      <ul className="footer_content_list">
        {ServicesConstant.map((item) => (
          <li key={item.path}>
            <Link to={item.path} className="footer_content_list_link">
              <DoubleRightOutlined aria-hidden /> {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
});

const SocialLink: FC = memo(function SocialLink() {
  const youtube = process.env.REACT_APP_YOUTUBE;
  const linkedin = process.env.REACT_APP_LINKEDIN;
  const instagram = process.env.REACT_APP_INSTAGRAM;

  return (
    <div>
      <div className="footer_content_title mb-2">Social Links</div>
      <p className="footer_content_social_intro">
        Stay connected with us on social media for updates and insights.
      </p>
      <div className="footer_content_icons_wrapper mt-3">
        {youtube && (
          <a
            href={youtube}
            target="_blank"
            rel="noopener noreferrer"
            className="footer_content_icons"
            title="Watch our videos on YouTube"
            aria-label="Follow us on YouTube"
          >
            <YoutubeFilled />
          </a>
        )}
        {linkedin && (
          <a
            href={linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="footer_content_icons"
            title="Read our posts on LinkedIn"
            aria-label="Connect with us on LinkedIn"
          >
            <LinkedinFilled />
          </a>
        )}
        {instagram && (
          <a
            href={instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="footer_content_icons"
            title="Follow us on Instagram"
            aria-label="Follow us on Instagram"
          >
            <InstagramOutlined />
          </a>
        )}
      </div>
    </div>
  );
});

const Copyright: FC = memo(function Copyright() {
  const currentYear = new Date().getFullYear();
  const siteName = process.env.REACT_APP_SITE_NAME;

  return (
    <div className="footer_copyright">
      Copyright &copy; 2023&ndash;{currentYear}{" "}
      <strong>
        <span className="company-name">{siteName}</span>
      </strong>{" "}
      All Rights Reserved.
    </div>
  );
});

function OrganizationSchema() {
  const siteUrl = process.env.REACT_APP_SITE_URL || "";
  const siteName = process.env.REACT_APP_SITE_NAME || "BIGELEMENT INFOTECH";
  const email = process.env.REACT_APP_EMAIL || "";
  const phone = process.env.REACT_APP_PHONE1 || "";

  const schema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteName,
    url: siteUrl,
    logo: `${siteUrl}/assets/images/logo.svg`,
    description:
      "BIGELEMENT INFOTECH - Web development, product development, and UI/UX design services in Surat, Gujarat, India.",
    address: {
      "@type": "PostalAddress",
      streetAddress:
        "B 712-713, IT Park, Opp. Shell Petrol Pump, Mota Varachha Main Road, Uttran",
      addressLocality: "Surat",
      postalCode: "394105",
      addressRegion: "Gujarat",
      addressCountry: "IN",
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: phone,
      email,
      contactType: "customer service",
      areaServed: "IN",
    },
    sameAs: [
      process.env.REACT_APP_YOUTUBE,
      process.env.REACT_APP_LINKEDIN,
      process.env.REACT_APP_INSTAGRAM,
    ].filter(Boolean),
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default memo(MainFooter);
